import * as React from 'react';
import styled from 'styled-components';
import { useMutation } from 'urql';

import Linkify from 'linkify-react';
import * as colors from '../../shared/colors';
import { formatYMD, parseDate } from '../../shared/dateFns';
import t from '../../shared/translations';
import PrevMessage from './PrevMessage';
import { IMessage } from './query';

const markReadMutation = `
    mutation($id:String!) {
        messageToggleRead(id:$id,read:true) {
            error
            message {
                __typename
            }
        }
    }
`;

const archiveMutataion = `
    mutation($id:String!) {
        messageToggleArchived(id:$id,archived:true) {
            error
            message {
                __typename
            }
        }
    }
`;

interface IArgs {
    id: string;
}

const MessageWrapper = styled.div`
    border: 1px solid ${colors.text};
    margin: 0 0 1px 0;
    border-radius: 0.5rem;
    padding: 0.5rem;
`;

interface IMessageHead {
    $unread: boolean;
}

const MessageHead = styled.div<IMessageHead>`
    cursor: pointer;
    font-weight: ${(({ $unread }) => $unread ? 'bold' : 'normal')};
    display: flex;
    flex-wrap: wrap;
`;

const HeaderDate = styled.div`
    flex-basis: 100px;
    flex-grow: 1;
    padding: 4px;
`;

const HeaderSender = styled.div`
    flex-basis: 150px;
    flex-grow: 1;
    padding: 4px;
`;

const HeaderSubject = styled.div`
    flex-basis: 400px;
    flex-grow: 1;
    padding: 4px;
`;

const Body = styled.div`
    margin: 0.5rem 0 0 0;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
`;

const Actions = styled.div`
    margin: 0.5rem 0 0 0;
`;

interface IProps {
    message: IMessage;
    open: boolean;
    toggleOpen: (id: string) => void;
    openReply: (message: IMessage) => void;
}

function Message({ message, open, toggleOpen, openReply }: IProps): JSX.Element {
    const [readResult, executeRead] = useMutation<any, IArgs>(markReadMutation);
    const [archiveResult, executeArchive] = useMutation<any, IArgs>(archiveMutataion);

    const clickHeader = () => {
        if (!message.read && !readResult.fetching) {
            void executeRead({ id: message.id });
        }
        toggleOpen(message.id);
    };

    const clickArchive = (event: React.MouseEvent<HTMLAnchorElement>): boolean => {
        event.preventDefault();
        if (!archiveResult.fetching) {
            void executeArchive({ id: message.id });
        }
        return false;
    };

    const clickReply = (event: React.MouseEvent<HTMLAnchorElement>): boolean => {
        event.preventDefault();
        openReply(message);
        return false;
    };

    return <MessageWrapper>
        <MessageHead $unread={!message.read} onClick={clickHeader}>
            <HeaderDate>
                {formatYMD(parseDate(message.datetime))}
            </HeaderDate>
            <HeaderSender>
                {message.senderName}
            </HeaderSender>
            <HeaderSubject>
                {message.subject}
            </HeaderSubject>
        </MessageHead>
        {open && <Body>
            <PrevMessage message={message.replyTo} />
            <div>
                <Linkify options={{ nl2br: true }}>{message.content}</Linkify>
            </div>
            <Actions>
                {!message.archived && (
                    <a href='#' onClick={clickArchive}>
                        {t('player.messages.button-archive-message')}{' '}
                    </a>
                )}
                {message.canReply && (
                    <a href='#' onClick={clickReply}>
                        {t('player.messages.button-reply')}
                    </a>
                )}
            </Actions>
        </Body>}
    </MessageWrapper>;
}

export default React.memo(Message);
